import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'
import { GemsApi_StoneCategory, type StoneFragment } from '@/graphql/api'
import { assertNever } from '@/helpers'

export type StoneShapeIcon = {
    url: string
    alt: string
}

const missingValue = '•'

export const getStoneShapeIcon = (
    stone: RingBuilderStoneTypes
): StoneShapeIcon | undefined => {
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            return (
                (stone.shape.icon?.url && {
                    url: stone.shape.icon?.url,
                    alt: stone.shape.translation.name,
                }) ||
                undefined
            )
        case 'GemsApi_Stone':
            return (
                (stone.shapeWithInfo.info?.icon?.url && {
                    url: stone.shapeWithInfo.info?.icon?.url,
                    alt: stone.shapeWithInfo.info?.translation.name,
                }) ||
                undefined
            )
        default:
            return assertNever(stone, undefined)
    }
}

export const getStoneWeight = (stone: RingBuilderStoneTypes): string => {
    let weight = 0
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            weight = Number(stone.caratWeight)
            break
        case 'GemsApi_Stone':
            weight = stone.weight
            break
        default:
            weight = assertNever(stone, 0)
    }

    return weight.toFixed(2)
}

export const getStoneColor = (stone: RingBuilderStoneTypes): string => {
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            return stone.color.translation.name
        case 'GemsApi_Stone':
            return (
                stone.colorWithInfo.info?.translation.name ||
                stone.colorWithInfo.code
            )
        default:
            return assertNever(stone, missingValue)
    }
}

export const getStoneClarity = (stone: RingBuilderStoneTypes): string => {
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            return stone.clarity.translation.name
        case 'GemsApi_Stone':
            return (
                stone.clarityWithInfo.info?.translation.name ||
                stone.clarityWithInfo.code
            )
        default:
            return assertNever(stone, missingValue)
    }
}

export const getStoneCut = (stone: RingBuilderStoneTypes): string => {
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            return missingValue
        case 'GemsApi_Stone':
            return (
                stone.cutWithInfo?.info?.translation.name ||
                stone.cutWithInfo?.code ||
                missingValue
            )
        default:
            return assertNever(stone, missingValue)
    }
}

export const getStonePolish = (stone: RingBuilderStoneTypes): string => {
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            return missingValue
        case 'GemsApi_Stone':
            return (
                stone.polishWithInfo?.info?.abbreviation ||
                stone.polishWithInfo?.code ||
                missingValue
            )
        default:
            return assertNever(stone, missingValue)
    }
}

export const getStoneSymmetry = (stone: RingBuilderStoneTypes): string => {
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            return missingValue
        case 'GemsApi_Stone':
            return (
                stone.symmetryWithInfo?.info?.abbreviation ||
                stone.symmetryWithInfo?.code ||
                missingValue
            )
        default:
            return assertNever(stone, missingValue)
    }
}

export const getStoneFluorescence = (stone: RingBuilderStoneTypes): string => {
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            return missingValue
        case 'GemsApi_Stone':
            return (
                stone.fluorescenceWithInfo?.info?.abbreviation ||
                stone.fluorescenceWithInfo?.code ||
                missingValue
            )
        default:
            return assertNever(stone, missingValue)
    }
}

export const getIsExpress = (stone: RingBuilderStoneTypes): boolean => {
    if (stone.__typename !== 'GemsApi_Stone') {
        return false
    }

    if (stone.deliveryDays === null) {
        return false
    }

    return stone.deliveryDays <= 2
}

export function getGemsApiStoneTitle(
    stone: StoneFragment
): `${string} ${string}ct` {
    return `${stone.shapeWithInfo.info?.translation.name || stone.shapeWithInfo.code} ${stone.weight.toFixed(2)}ct`
}

export const getStoneTitle = (stone: RingBuilderStoneTypes): string => {
    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            return `${stone.shape.translation.name} ${Number(
                stone.caratWeight
            ).toFixed(2)}ct`
        case 'GemsApi_Stone':
            return getGemsApiStoneTitle(stone)
        default:
            return assertNever(stone, '')
    }
}

export const getGemsApiStoneSubtitle = (stone: StoneFragment): string => {
    if (GemsApi_StoneCategory.Gemstone === stone.categoryWithInfo.code) {
        return `${
            stone.colorWithInfo.info?.translation.name ||
            stone.colorWithInfo.code
        }, ${
            stone.clarityWithInfo.info?.translation.name ||
            stone.clarityWithInfo.code
        }`
    }

    let subtitle = `${
        stone.colorWithInfo?.info?.translation.name || stone.colorWithInfo.code
    }, ${
        stone.clarityWithInfo?.info?.abbreviation || stone.clarityWithInfo.code
    }`

    const cut = stone.cutWithInfo

    if (cut) {
        subtitle = `${subtitle} ${cut.info?.abbreviation || cut.code}`
    }

    return subtitle
}

export const getGemsApiStoneConsultantInfo = (
    stone: RingBuilderStoneTypes,
    isConsultant: boolean
): string[] => {
    if (stone.__typename !== 'GemsApi_Stone' || !isConsultant) {
        return []
    }

    const info = [
        [
            stone.polishWithInfo?.info?.abbreviation,
            stone.symmetryWithInfo?.info?.abbreviation,
            stone.fluorescenceWithInfo?.info?.abbreviation,
        ]
            .filter(Boolean)
            .join(', '),
        [
            stone.length && stone.width && stone.depth
                ? `${stone.length} × ${stone.width} × ${stone.depth}`
                : undefined,
            stone.ratio,
            stone.tablePercent ? `${stone.tablePercent}%` : undefined,
            stone.depthPercent ? `${stone.depthPercent}%` : undefined,
        ]
            .filter(Boolean)
            .join(', '),
    ]

    if (stone.deliveryDays) {
        info.push(`Delivery up to ${stone.deliveryDays} days`)
    }

    return info
}

export const getStoneSubtitle = (stone: RingBuilderStoneTypes): string => {
    if ('GemsApi_Stone' === stone.__typename) {
        return getGemsApiStoneSubtitle(stone)
    }

    if (
        // TH-15089 Toggle delete lines -->
        'NonSkuGemstone' === stone.__typename ||
        // TH-15089 Toggle delete lines <--
        'NonSkuLabGrown' === stone.__typename
    ) {
        return `${stone.color.translation.name}, ${stone.clarity.translation.name}`
    }

    return assertNever(stone, '')
}
